import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";

import { Values } from "@/constants/values";
import { sanitizeKeyForCSS } from "@/hooks/useContextTree";

// ============================================================================
// TYPES
// ============================================================================

interface ColumnConfig {
  objectKey: string;
  width?: number; // Pixel width, or a share of the container for a "percentage" column
  minWidth?: number; // Min width in pixels
  maxWidth?: number; // Max width in pixels
  /** When "percentage", the column grows/shrinks with the container.
   *  The initial `width` value becomes the CSS min-width.
   *  Resizing adjusts percentages rather than fixed pixel widths. */
  widthMode?: "pixel" | "percentage";
}

interface UseResizeColumnOptions {
  /** Minimum width for all columns (can be overridden per-column) */
  minWidth?: number;
  /** Maximum width for all columns (can be overridden per-column) */
  maxWidth?: number;
  /** Unique context ID for CSS variable namespacing */
  contextId?: string;
  /** Initial offset in pixels */
  initialOffset?: number;
  /** Width of the container element in pixels — required when any column uses widthMode "percentage" */
  containerWidth?: number;
  /** Callback when a column is resized */
  onColumnResize?: (objectKey: string, left: number, width: number) => void;
}

// What a drag began from
interface PixelDraggingState {
  objectKey: string;
  startX: number;
  startWidth: number;
  /** Starting percentage (only set for percentage-widthMode columns) */
  startPercentage?: number;
  initialWidths: { [key: string]: number };
  /** Starting percentages snapshot (only for percentage-widthMode columns) */
  initialPercentages: { [key: string]: number };
  isMirrored: boolean;
}

// ============================================================================
// HOOK
// ============================================================================

export function useResizeColumn(
  givenColumns: ColumnConfig[],
  options: UseResizeColumnOptions = {},
): {
  handleMouseDown: (
    objectKey: string,
    isMirrored?: boolean,
  ) => (e: React.MouseEvent) => void;
  getColumnPositions: () => { [key: string]: { left: number; width: number } };
  getColumnWidths: () => { [key: string]: number };
  getColumnPercentages: () => { [key: string]: number };
} {
  const columns = useMemo(
    () =>
      givenColumns
        .filter((column) => column?.objectKey)
        .map((column) => ({
          ...column,
          label: column.objectKey.replace(/[^a-zA-Z0-9-]/g, "_").toLowerCase(),
        })),
    [givenColumns],
  );
  const minWidth = options.minWidth ?? DEFAULT_MIN_WIDTH;
  const maxWidth = options.maxWidth ?? DEFAULT_MAX_WIDTH;
  const contextId = options.contextId ?? DEFAULT_CONTEXT_ID;
  const initialOffset = options.initialOffset ?? DEFAULT_INITIAL_OFFSET;
  const containerWidth = options.containerWidth ?? DEFAULT_CONTAINER_WIDTH;
  const onColumnResize = options.onColumnResize;

  const draggingRef = useRef<PixelDraggingState | null>(null);
  const styleSheetRef = useRef<HTMLStyleElement | null>(null);
  const columnWidthsRef = useRef<{ [key: string]: number }>({});
  const columnPercentagesRef = useRef<{ [key: string]: number }>({});
  const columnPositionsRef = useRef<{
    [key: string]: { left: number; width: number };
  }>({});
  // Track the sorted column key signature to detect actual column set changes
  // (vs mere reordering or container-width-only changes)
  const columnsKeyRef = useRef<string>("");

  // Only reflow from container size when percentage columns need pixel conversion.
  // Pixel-only tables must ignore live containerWidth so panel resize stays CSS-only.
  const hasPercentageColumns = columns.some(
    (c) => c?.widthMode === "percentage",
  );
  const layoutContainerWidth = hasPercentageColumns ? containerWidth : 0;

  // Compute the available width for percentage-mode columns:
  // container minus the initial offset and the widths of all pixel-mode columns.
  const getAvailableWidth = useCallback(
    (pixelWidths: { [key: string]: number }) => {
      if (layoutContainerWidth <= 0) return 1;
      let fixedWidth = initialOffset;
      columns.forEach((col) => {
        if (!col?.objectKey || col.widthMode === "percentage") return;
        fixedWidth +=
          pixelWidths[col.objectKey] ||
          col.width ||
          Values.DEFAULT_COLUMN_WIDTH;
      });
      return Math.max(1, layoutContainerWidth - fixedWidth);
    },
    [columns, initialOffset, layoutContainerWidth],
  );

  useLayoutEffect(() => {
    if (!columns || columns.length === 0) return;

    // Detect when the set of column keys changes (not just reordering or container resize)
    const newColumnsKey = [...columns]
      .map((c) => c?.objectKey || "")
      .sort()
      .join(",");
    const columnsChanged = newColumnsKey !== columnsKeyRef.current;
    columnsKeyRef.current = newColumnsKey;

    const currentWidthCache = columnWidthsRef.current;
    const currentPctCache = columnPercentagesRef.current;

    // --- Step 1a: Update pixel-mode column widths (cache wins over props) ---
    const updatedWidthCache: { [key: string]: number } = {};
    columns.forEach((column) => {
      if (!column?.objectKey || column.widthMode === "percentage") return;
      updatedWidthCache[column.objectKey] =
        !columnsChanged && currentWidthCache[column.objectKey] !== undefined
          ? currentWidthCache[column.objectKey]
          : column.width || Values.DEFAULT_COLUMN_WIDTH;
    });

    // --- Step 1b: Update percentage-mode column percentages ---
    if (hasPercentageColumns) {
      const pctColumns = columns.filter((c) => c?.widthMode === "percentage");
      const totalPctInitialWidth = pctColumns.reduce(
        (sum, c) => sum + (c.width || Values.DEFAULT_COLUMN_WIDTH),
        0,
      );

      const updatedPctCache: { [key: string]: number } = {};
      pctColumns.forEach((col) => {
        if (!col?.objectKey) return;
        // Preserve existing percentage unless columns changed
        updatedPctCache[col.objectKey] =
          !columnsChanged && currentPctCache[col.objectKey] !== undefined
            ? currentPctCache[col.objectKey]
            : totalPctInitialWidth > 0
              ? ((col.width || Values.DEFAULT_COLUMN_WIDTH) /
                  totalPctInitialWidth) *
                100
              : 100 / pctColumns.length;
      });
      columnPercentagesRef.current = updatedPctCache;

      // Derive pixel widths from percentages
      const availableWidth = getAvailableWidth(updatedWidthCache);
      pctColumns.forEach((col) => {
        if (!col?.objectKey) return;
        const pct = updatedPctCache[col.objectKey] || 0;
        let width = Math.max(
          col.minWidth ?? col.width ?? minWidth,
          (pct / 100) * availableWidth,
        );
        if (col.maxWidth != null) {
          width = Math.min(width, col.maxWidth);
        }
        updatedWidthCache[col.objectKey] = width;
      });
    }

    columnWidthsRef.current = updatedWidthCache;

    // --- Step 2: Create / Update style tag ---
    if (!styleSheetRef.current) {
      styleSheetRef.current = document.createElement("style");
      document.head.appendChild(styleSheetRef.current);
    }

    const widthStyles = columns
      .map((column) => {
        if (!column || !column.objectKey) return "";
        const sanitizedKey = sanitizeKeyForCSS(column.objectKey);
        const currentWidth = columnWidthsRef.current[column.objectKey];
        const widthVar = `--${contextId}-column-${sanitizedKey}-width`;

        document.documentElement.style.setProperty(
          widthVar,
          `${currentWidth}px`,
        );

        if (column.widthMode === "percentage") {
          // Percentage-mode columns: CSS width grows/shrinks with container.
          // min-width is fixed at the initial pixel value from the prop.
          const pixelMinWidth = column.width ?? column.minWidth ?? minWidth;
          const maxWidthCss =
            column.maxWidth != null ? `max-width: ${column.maxWidth}px;` : "";
          return `.${contextId}-column-${sanitizedKey} {
            width: var(${widthVar});
            min-width: ${pixelMinWidth}px;
            ${maxWidthCss}
          }
          .${contextId}-column-${sanitizedKey}:focus,
          .${contextId}-column-${sanitizedKey}:active {
            min-width: ${pixelMinWidth}px;
            ${maxWidthCss}
          }`;
        }

        return `.${contextId}-column-${sanitizedKey} {
            width: var(${widthVar});
            min-width: var(${widthVar});
          }
          .${contextId}-column-${sanitizedKey}:focus,
          .${contextId}-column-${sanitizedKey}:active {
            min-width: var(${widthVar});
          }`;
      })
      .join("\n");
    styleSheetRef.current.innerHTML = widthStyles;

    // --- Step 3: Calculate and set initial offsets using cached widths ---
    let currentCumulativeWidth = initialOffset;
    const newPositions: { [key: string]: { left: number; width: number } } = {};

    columns.forEach((column) => {
      if (!column || !column.objectKey) return;
      const sanitizedKey = sanitizeKeyForCSS(column.objectKey);
      const offsetVar = `--${contextId}-column-${sanitizedKey}-offset`;

      document.documentElement.style.setProperty(
        offsetVar,
        `${currentCumulativeWidth}px`,
      );

      const cachedWidth =
        columnWidthsRef.current[column.objectKey] ||
        Values.DEFAULT_COLUMN_WIDTH;

      newPositions[column.objectKey] = {
        left: currentCumulativeWidth,
        width: cachedWidth,
      };

      onColumnResize?.(column.objectKey, currentCumulativeWidth, cachedWidth);
      currentCumulativeWidth += cachedWidth;
    });

    columnPositionsRef.current = newPositions;

    const totalWidthVar = `--${contextId}-total-width`;
    document.documentElement.style.setProperty(
      totalWidthVar,
      `${currentCumulativeWidth}px`,
    );

    return () => {
      if (styleSheetRef.current) {
        try {
          document.head.removeChild(styleSheetRef.current);
        } catch {
          /* ignore */
        }
        styleSheetRef.current = null;
      }

      columns.forEach((column) => {
        if (!column || !column.objectKey) return;
        const sanitizedKey = sanitizeKeyForCSS(column.objectKey);
        try {
          document.documentElement.style.removeProperty(
            `--${contextId}-column-${sanitizedKey}-width`,
          );
          document.documentElement.style.removeProperty(
            `--${contextId}-column-${sanitizedKey}-offset`,
          );
        } catch {
          /* ignore */
        }
      });
      try {
        document.documentElement.style.removeProperty(
          `--${contextId}-total-width`,
        );
      } catch {
        /* ignore */
      }
    };
  }, [
    columns,
    contextId,
    minWidth,
    maxWidth,
    initialOffset,
    layoutContainerWidth,
    hasPercentageColumns,
    onColumnResize,
    getAvailableWidth,
  ]);

  // Recalculate offsets from cache
  const recalculateAndSetOffsetsFromCache = useCallback(
    (fromVisualIndex: number | null = null) => {
      const startIndex = Math.max(
        0,
        fromVisualIndex === null ? 0 : fromVisualIndex,
      );
      let currentCumulativeWidth = initialOffset;
      let totalWidth = 0;

      const newPositions: { [key: string]: { left: number; width: number } } = {
        ...columnPositionsRef.current,
      };

      columns.forEach((column, index) => {
        if (!column || !column.objectKey) return;
        const width =
          columnWidthsRef.current[column.objectKey] ||
          Values.DEFAULT_COLUMN_WIDTH;
        if (index < startIndex) {
          currentCumulativeWidth += width;
        }
        totalWidth += width;
      });

      for (let i = startIndex; i < columns.length; i++) {
        const column = columns[i];
        if (!column || !column.objectKey) continue;
        const sanitizedKey = sanitizeKeyForCSS(column.objectKey);
        const offsetVar = `--${contextId}-column-${sanitizedKey}-offset`;
        const width =
          columnWidthsRef.current[column.objectKey] ||
          Values.DEFAULT_COLUMN_WIDTH;

        document.documentElement.style.setProperty(
          offsetVar,
          `${currentCumulativeWidth}px`,
        );
        onColumnResize?.(column.objectKey, currentCumulativeWidth, width);

        newPositions[column.objectKey] = {
          left: currentCumulativeWidth,
          width: width,
        };

        currentCumulativeWidth += width;
      }

      columnPositionsRef.current = newPositions;

      const totalWidthVar = `--${contextId}-total-width`;
      document.documentElement.style.setProperty(
        totalWidthVar,
        `${totalWidth + initialOffset}px`,
      );
    },
    [columns, contextId, initialOffset, onColumnResize],
  );

  const handleMouseDown = useCallback(
    (objectKey: string, isMirrored: boolean = false) => {
      return (e: React.MouseEvent) => {
        const columnConfig = columns.find((c) => c.objectKey === objectKey);
        if (!columnConfig) return;

        let startWidth = Values.DEFAULT_COLUMN_WIDTH;
        try {
          startWidth =
            columnWidthsRef.current[objectKey] || Values.DEFAULT_COLUMN_WIDTH;
        } catch {
          startWidth = columnConfig?.width || Values.DEFAULT_COLUMN_WIDTH;
        }

        document.body.style.userSelect = "none";

        draggingRef.current = {
          objectKey: objectKey,
          startX: e.clientX,
          startWidth: startWidth,
          startPercentage:
            columnConfig.widthMode === "percentage"
              ? (columnPercentagesRef.current[objectKey] ?? 0)
              : undefined,
          isMirrored: isMirrored,
          initialWidths: { ...columnWidthsRef.current },
          initialPercentages: { ...columnPercentagesRef.current },
        };
      };
    },
    [columns],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!draggingRef.current) return;

      const {
        objectKey: draggedObjectKey,
        startX,
        startWidth,
        startPercentage,
        isMirrored,
        initialPercentages,
      } = draggingRef.current;
      if (!draggedObjectKey) return;

      const columnConfig = columns.find(
        (col) => col.objectKey === draggedObjectKey,
      );
      if (!columnConfig) return;

      const sanitizedKey = sanitizeKeyForCSS(draggedObjectKey);
      const delta = isMirrored ? startX - e.clientX : e.clientX - startX;

      if (columnConfig.widthMode === "percentage") {
        // --- Percentage-mode column resize ---
        const availableWidth = getAvailableWidth(columnWidthsRef.current);
        const draggedMinWidth =
          columnConfig.minWidth ?? columnConfig.width ?? minWidth;
        const draggedMinPct = (draggedMinWidth / availableWidth) * 100;

        const pctDelta = (delta / availableWidth) * 100;
        const initialPct =
          startPercentage ?? initialPercentages[draggedObjectKey] ?? 0;

        let newDraggedPct = Math.max(draggedMinPct, initialPct + pctDelta);

        const draggedIndex = columns.findIndex(
          (c) => c.objectKey === draggedObjectKey,
        );
        const nextColumn =
          draggedIndex < columns.length - 1 ? columns[draggedIndex + 1] : null;

        // When there is a next column, cap expansion so the adjacent column
        // cannot shrink below its minimum.
        if (nextColumn) {
          const nextInitialPct = initialPercentages[nextColumn.objectKey] ?? 0;
          const nextMinWidth =
            nextColumn.minWidth ?? nextColumn.width ?? minWidth;
          const nextMinPct = (nextMinWidth / availableWidth) * 100;
          const maxDeltaPct = nextInitialPct - nextMinPct;
          newDraggedPct = Math.min(newDraggedPct, initialPct + maxDeltaPct);
        }

        const newDraggedWidth = Math.max(
          draggedMinWidth,
          (newDraggedPct / 100) * availableWidth,
        );

        columnPercentagesRef.current[draggedObjectKey] = newDraggedPct;
        columnWidthsRef.current[draggedObjectKey] = newDraggedWidth;

        document.documentElement.style.setProperty(
          `--${contextId}-column-${sanitizedKey}-width`,
          `${newDraggedWidth}px`,
        );

        if (columnPositionsRef.current[draggedObjectKey]) {
          columnPositionsRef.current[draggedObjectKey] = {
            ...columnPositionsRef.current[draggedObjectKey],
            width: newDraggedWidth,
          };
          onColumnResize?.(
            draggedObjectKey,
            columnPositionsRef.current[draggedObjectKey].left,
            newDraggedWidth,
          );
        }

        // Update the adjacent column to absorb the change
        if (nextColumn) {
          const actualPctDelta = newDraggedPct - initialPct;
          const nextInitialPct = initialPercentages[nextColumn.objectKey] ?? 0;

          if (nextColumn.widthMode === "percentage") {
            const nextMinWidth =
              nextColumn.minWidth ?? nextColumn.width ?? minWidth;
            const nextMinPct = (nextMinWidth / availableWidth) * 100;
            const nextNewPct = Math.max(
              nextMinPct,
              nextInitialPct - actualPctDelta,
            );
            const nextNewWidth = Math.max(
              nextMinWidth,
              (nextNewPct / 100) * availableWidth,
            );
            columnPercentagesRef.current[nextColumn.objectKey] = nextNewPct;
            columnWidthsRef.current[nextColumn.objectKey] = nextNewWidth;

            const nextSanitizedKey = sanitizeKeyForCSS(nextColumn.objectKey);
            document.documentElement.style.setProperty(
              `--${contextId}-column-${nextSanitizedKey}-width`,
              `${nextNewWidth}px`,
            );
          } else {
            // Adjacent is a pixel-mode column: absorb in pixels
            const nextInitialWidth =
              draggingRef.current!.initialWidths[nextColumn.objectKey] ||
              nextColumn.width ||
              Values.DEFAULT_COLUMN_WIDTH;
            const pixelDelta = newDraggedWidth - startWidth;
            const nextMinWidth = nextColumn.minWidth ?? minWidth;
            const nextNewWidth = Math.max(
              nextMinWidth,
              nextInitialWidth - pixelDelta,
            );
            columnWidthsRef.current[nextColumn.objectKey] = nextNewWidth;

            const nextSanitizedKey = sanitizeKeyForCSS(nextColumn.objectKey);
            document.documentElement.style.setProperty(
              `--${contextId}-column-${nextSanitizedKey}-width`,
              `${nextNewWidth}px`,
            );
          }
        }

        recalculateAndSetOffsetsFromCache(draggedIndex + 1);
      } else {
        // --- Original pixel-mode column resize ---
        let newWidth = startWidth + delta;

        const columnMinWidth = columnConfig.minWidth ?? minWidth;
        const columnMaxWidth = columnConfig.maxWidth ?? maxWidth;

        newWidth = Math.max(columnMinWidth, Math.min(columnMaxWidth, newWidth));

        const draggedWidthVar = `--${contextId}-column-${sanitizedKey}-width`;
        document.documentElement.style.setProperty(
          draggedWidthVar,
          `${newWidth}px`,
        );

        columnWidthsRef.current[draggedObjectKey] = newWidth;

        if (columnPositionsRef.current[draggedObjectKey]) {
          columnPositionsRef.current[draggedObjectKey] = {
            ...columnPositionsRef.current[draggedObjectKey],
            width: newWidth,
          };
          onColumnResize?.(
            draggedObjectKey,
            columnPositionsRef.current[draggedObjectKey].left,
            newWidth,
          );
        }

        const visualIndexOfDraggedColumn = columns.findIndex(
          (col) => col && col.objectKey === draggedObjectKey,
        );

        if (visualIndexOfDraggedColumn === -1) return;

        recalculateAndSetOffsetsFromCache(visualIndexOfDraggedColumn + 1);
      }
    },
    [
      columns,
      minWidth,
      maxWidth,
      contextId,
      recalculateAndSetOffsetsFromCache,
      onColumnResize,
      getAvailableWidth,
    ],
  );

  const handleMouseUp = useCallback(() => {
    if (!draggingRef.current) return;
    document.body.style.userSelect = "";
    draggingRef.current = null;
  }, []);

  useEffect(() => {
    const handleMouseMoveWithRAF = (e: MouseEvent) => {
      requestAnimationFrame(() => handleMouseMove(e));
    };

    document.addEventListener("mousemove", handleMouseMoveWithRAF);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMoveWithRAF);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return useMemo(
    () => ({
      handleMouseDown,
      getColumnPositions: () => columnPositionsRef.current,
      getColumnWidths: () => columnWidthsRef.current,
      getColumnPercentages: () => columnPercentagesRef.current,
    }),
    [handleMouseDown],
  );
}

const DEFAULT_MIN_WIDTH = Values.DEFAULT_ROW_HEIGHT;
const DEFAULT_MAX_WIDTH = 400;
const DEFAULT_CONTEXT_ID = "table";
const DEFAULT_INITIAL_OFFSET = 0;
const DEFAULT_CONTAINER_WIDTH = 0;
